import { ApiError, NetworkError } from './client';

/**
 * Что показать человеку, когда сервер отказал.
 *
 * Сервер отвечает ДВУМЯ уровнями: общей фразой «Проверьте заполнение
 * полей» и списком полей с человеческими причинами — «categoryId:
 * Поле обязательно» переводится в понятное ещё на сервере (правило 139).
 *
 * Показывал этот список ОДИН экран из шестидесяти — приём звонка, ради
 * которого правило и заводили. Во всех прочих формах — платёж, доп.
 * соглашение, реквизиты, бригады — человек видел только общую фразу и
 * не знал, какое поле поправить. Отказ, из которого не видно, что
 * делать, — это половина отказа (та же беда, что у правила 149 про
 * цифру в предупреждении).
 */
/**
 * Как поле называется НА ЭКРАНЕ.
 *
 * Сервер называет поля так, как они лежат в схеме: `clientId`,
 * `scheduledAt`, `monthlyPercent`. Показать это оператору — то же
 * самое, что показать «categoryId: Required», от чего уводило правило
 * 139: он не понимает ни поля, ни требования, и зовёт того, кто ставил
 * программу.
 *
 * Словарь неполный намеренно: незнакомое поле показывается КАК ЕСТЬ.
 * Техническое имя хуже человеческого, но лучше молчания — по нему хотя
 * бы понятно, куда смотреть, и его можно назвать в разговоре.
 */
const FIELD_NAMES: Record<string, string> = {
  clientId: 'Клиент',
  categoryId: 'Направление работ',
  dealId: 'Сделка',
  measurementId: 'Замер',
  brigadeId: 'Бригада',
  userId: 'Сотрудник',
  surveyorId: 'Замерщик',
  amount: 'Сумма',
  totalAmount: 'Сумма договора',
  method: 'Способ оплаты',
  paymentMethod: 'Способ оплаты',
  prepayment: 'Предоплата',
  discountValue: 'Скидка',
  phone: 'Телефон',
  phoneExtra: 'Дополнительный телефон',
  login: 'Логин',
  password: 'Пароль',
  fullName: 'Имя',
  address: 'Адрес',
  city: 'Город',
  scheduledAt: 'Дата и время',
  date: 'Дата',
  validFrom: 'Действует с',
  validTo: 'Действует по',
  reason: 'Причина',
  comment: 'Комментарий',
  contractNo: 'Номер договора',
  receiptNo: 'Номер квитанции',
  quantity: 'Количество',
  unitPrice: 'Цена',
  costPrice: 'Себестоимость',
  monthlyAmount: 'Взнос в месяц',
  monthlyPercent: 'Доля от зарплаты',
  score: 'Оценка',
  widthMm: 'Ширина',
  lengthMm: 'Длина',
  rollWidthMm: 'Ширина рулона',
  shrinkagePercent: 'Усадка',
  harpoon: 'Гарпун',
  inn: 'ИНН',
  kpp: 'КПП',
  status: 'Статус',
};

/** Название поля для человека; незнакомое остаётся как есть */
function fieldTitle(field: string): string {
  if (field === '(форма)') return 'Форма';
  return FIELD_NAMES[field] ?? field;
}

export function describeApiError(error: unknown, fallback = 'Попробуйте ещё раз'): string {
  if (error instanceof NetworkError) {
    // Про связь человек и так знает по индикатору — но своими словами
    return 'Нет связи с сервером';
  }
  if (!(error instanceof ApiError)) return fallback;
  if (error.fieldErrors?.length) {
    return error.fieldErrors.map((e) => `${fieldTitle(e.field)}: ${e.message}`).join('; ');
  }
  return error.message || fallback;
}
